/**
 * Live observability dashboard fed by `/api/metrics/stream` (SSE).
 * Falls back to polling if SSE fails.
 *
 * Sections:
 *  - 4 KPI cards : p95, error rate, requests count, uptime
 *  - Sparkline-style p95 trend (last 60 buckets / 30 min)
 *  - Latency histogram bar chart
 *  - Top 10 slowest endpoints table
 *  - Recent slow requests (>2s) list
 */
import { useEffect, useRef, useState } from "react";
import {
  Activity,
  AlertTriangle,
  Clock,
  Server,
  TrendingUp,
  Zap,
  Wifi,
  WifiOff,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import PageHeader from "../components/PageHeader";

const API = process.env.REACT_APP_BACKEND_URL;

function formatUptime(seconds) {
  if (!seconds && seconds !== 0) return "—";
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}j ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m ${seconds % 60}s`;
}

function formatTime(ts) {
  try {
    return new Date(ts * 1000).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

const Kpi = ({ icon: Icon, label, value, sub, accent = "#f7c935", testId }) => (
  <div
    className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4 stagger-item"
    data-testid={testId}
  >
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs text-zinc-400 uppercase tracking-wider">{label}</span>
      <Icon className="w-4 h-4" style={{ color: accent }} />
    </div>
    <div className="text-3xl font-black tracking-tight" style={{ color: accent }}>
      {value}
    </div>
    {sub && <div className="text-[11px] text-zinc-500 mt-1">{sub}</div>}
  </div>
);

const HistoBar = ({ buckets }) => {
  const entries = Object.entries(buckets || {});
  const max = Math.max(1, ...entries.map(([, v]) => v));
  return (
    <div
      className="space-y-1.5"
      data-testid="histogram-chart"
    >
      {entries.map(([bucket, count]) => {
        const pct = (count / max) * 100;
        const color = bucket.startsWith(">") ? "#ef4444" : bucket.includes("2000") || bucket.includes("5000") ? "#f59e0b" : "#10b981";
        return (
          <div key={bucket} className="flex items-center gap-2 text-xs">
            <div className="w-16 text-zinc-500 tabular-nums">{bucket}</div>
            <div className="flex-1 h-5 bg-zinc-900 rounded overflow-hidden">
              <div
                className="h-full transition-all duration-300"
                style={{ width: `${pct}%`, background: color }}
              />
            </div>
            <div className="w-12 text-right text-zinc-300 tabular-nums">{count}</div>
          </div>
        );
      })}
    </div>
  );
};

const Sparkline = ({ data, accessor, color = "#f7c935" }) => {
  if (!data || data.length < 2) {
    return <div className="text-xs text-zinc-500 py-8 text-center">Données insuffisantes (attente ~30s)…</div>;
  }
  const values = data.map(accessor);
  const max = Math.max(...values, 1);
  const min = Math.min(...values);
  const w = 600;
  const h = 80;
  const stepX = w / (data.length - 1 || 1);
  const range = max - min || 1;
  const points = values
    .map((v, i) => {
      const x = i * stepX;
      const y = h - ((v - min) / range) * (h - 8) - 4;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      className="w-full h-20"
      data-testid="sparkline-chart"
    >
      <polyline fill="none" stroke={color} strokeWidth="1.6" points={points} />
      <circle
        cx={(data.length - 1) * stepX}
        cy={h - ((values[values.length - 1] - min) / range) * (h - 8) - 4}
        r="3"
        fill={color}
      />
    </svg>
  );
};

export default function ObservabilityPage() {
  const { user } = useAuth();
  const [snap, setSnap] = useState(null);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);
  const esRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    let pollHandle = null;

    // Try SSE first — Iter 253.1 cookie-only auth
    const url = `${API}/api/metrics/stream`;
    try {
      const es = new EventSource(url, { withCredentials: true });
      esRef.current = es;
      es.onopen = () => !cancelled && setConnected(true);
      es.onmessage = (evt) => {
        if (cancelled) return;
        try {
          const data = JSON.parse(evt.data);
          setSnap(data);
        } catch {
          /* ignore malformed */
        }
      };
      es.onerror = () => {
        // Browser-side SSE doesn't expose status codes — fall back to polling
        if (cancelled) return;
        setConnected(false);
        es.close();
        startPolling();
      };
    } catch {
      startPolling();
    }

    function startPolling() {
      const tick = async () => {
        if (cancelled) return;
        try {
          const r = await fetch(`${API}/api/metrics`, { credentials: "include" });
          if (!r.ok) throw new Error(String(r.status));
          const d = await r.json();
          if (!cancelled) {
            setSnap({ ts: d.now, uptime_s: d.uptime_s, http: d.http });
            setConnected(false); // polling fallback
            setError(null);
          }
        } catch (e) {
          if (!cancelled) setError(String(e));
        }
      };
      tick();
      pollHandle = setInterval(tick, 3000);
    }

    return () => {
      cancelled = true;
      if (esRef.current) esRef.current.close();
      if (pollHandle) clearInterval(pollHandle);
    };
  }, [user]);

  const http = snap?.http || {};
  const lat = http.global_latency_ms || {};
  const family = http.status_family || {};

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto" data-testid="observability-page">
      <PageHeader
        icon={<Activity />}
        title="Observabilité"
        description="Métriques HTTP en temps réel — p50/p95/p99, error rate, top endpoints lents"
        testId="observability-header"
        actions={(
          <span
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${
              connected
                ? "bg-green-500/10 text-green-400 border-green-500/30"
                : "bg-zinc-500/10 text-zinc-400 border-zinc-500/30"
            }`}
            data-testid="obs-connection-indicator"
          >
            {connected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
            {connected ? "SSE connecté" : "Polling"}
          </span>
        )}
      />

      {error && (
        <div className="mb-4 px-3 py-2 rounded-lg bg-red-500/10 text-red-400 text-xs border border-red-500/30">
          Erreur de connexion : {error}
        </div>
      )}

      {/* KPIs */}
      <div className="stagger grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Kpi
          icon={Zap}
          label="Latence p95"
          value={lat.p95 != null ? `${lat.p95}ms` : "—"}
          sub={`p50 ${lat.p50 ?? "—"}ms · p99 ${lat.p99 ?? "—"}ms`}
          accent={lat.p95 > 1000 ? "#ef4444" : lat.p95 > 500 ? "#f59e0b" : "#10b981"}
          testId="kpi-p95"
        />
        <Kpi
          icon={AlertTriangle}
          label="Error rate"
          value={`${http.error_rate_pct ?? 0}%`}
          sub={`${family["5xx"] ?? 0} sur ${http.total_requests_since_start ?? 0} requêtes`}
          accent={http.error_rate_pct > 1 ? "#ef4444" : "#10b981"}
          testId="kpi-error-rate"
        />
        <Kpi
          icon={TrendingUp}
          label="Requêtes"
          value={http.total_requests_since_start ?? 0}
          sub={`${http.sample_size ?? 0} dans la fenêtre`}
          accent="#3b82f6"
          testId="kpi-total-requests"
        />
        <Kpi
          icon={Server}
          label="Uptime"
          value={formatUptime(snap?.uptime_s)}
          sub={user?.role === "superadmin" ? "Process actuel" : ""}
          accent="#a855f7"
          testId="kpi-uptime"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* p95 trend sparkline */}
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4" data-testid="p95-trend-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-zinc-200 flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#f7c935]" /> p95 sur les 30 dernières minutes
            </h3>
            <span className="text-[10px] text-zinc-500">buckets de 30s</span>
          </div>
          <Sparkline
            data={http.time_series_30s || []}
            accessor={(d) => d.p95_ms || 0}
            color="#f7c935"
          />
          {http.time_series_30s && http.time_series_30s.length > 1 && (
            <div className="text-[11px] text-zinc-500 mt-1 flex justify-between">
              <span>{formatTime(http.time_series_30s[0]?.ts)}</span>
              <span>{formatTime(http.time_series_30s[http.time_series_30s.length - 1]?.ts)}</span>
            </div>
          )}
        </div>

        {/* Latency histogram */}
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4" data-testid="histogram-card">
          <h3 className="text-sm font-bold text-zinc-200 mb-3 flex items-center gap-2">
            <Activity className="w-4 h-4 text-[#f7c935]" /> Distribution des latences
          </h3>
          <HistoBar buckets={http.histogram_ms} />
        </div>
      </div>

      {/* Slowest endpoints */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 mb-6" data-testid="slowest-endpoints-card">
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
          <h3 className="text-sm font-bold text-zinc-200">Top 10 endpoints les plus lents</h3>
          <span className="text-[10px] text-zinc-500">trié par p95</span>
        </div>
        {(http.slowest_endpoints_top10 || []).length === 0 ? (
          <div className="px-4 py-10 text-center text-xs text-zinc-500">Aucun endpoint sondé</div>
        ) : (
          <div className="divide-y divide-zinc-800/60 text-xs">
            <div className="grid grid-cols-12 px-4 py-2 text-zinc-500 uppercase tracking-wider text-[10px]">
              <div className="col-span-6">Endpoint</div>
              <div className="col-span-1 text-right">Calls</div>
              <div className="col-span-1 text-right">p50</div>
              <div className="col-span-1 text-right">p95</div>
              <div className="col-span-1 text-right">p99</div>
              <div className="col-span-1 text-right">avg</div>
              <div className="col-span-1 text-right">5xx</div>
            </div>
            {http.slowest_endpoints_top10.map((e, i) => (
              <div key={`${e.method}-${e.path}-${i}`} className="grid grid-cols-12 px-4 py-2 hover:bg-white/[0.02]">
                <div className="col-span-6 truncate text-zinc-200">
                  <span className="text-[10px] uppercase font-bold mr-2 px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400">
                    {e.method}
                  </span>
                  <span className="font-mono">{e.path}</span>
                </div>
                <div className="col-span-1 text-right text-zinc-400 tabular-nums">{e.samples}</div>
                <div className="col-span-1 text-right text-zinc-300 tabular-nums">{e.p50}ms</div>
                <div className="col-span-1 text-right font-bold tabular-nums" style={{ color: e.p95 > 1000 ? "#ef4444" : e.p95 > 500 ? "#f59e0b" : "#10b981" }}>
                  {e.p95}ms
                </div>
                <div className="col-span-1 text-right text-zinc-300 tabular-nums">{e.p99}ms</div>
                <div className="col-span-1 text-right text-zinc-500 tabular-nums">{e.avg}ms</div>
                <div className="col-span-1 text-right tabular-nums" style={{ color: e.errors > 0 ? "#ef4444" : "#52525b" }}>
                  {e.errors}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent slow requests */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/40" data-testid="recent-slow-card">
        <div className="px-4 py-3 border-b border-zinc-800">
          <h3 className="text-sm font-bold text-zinc-200">Requêtes lentes récentes (&gt;2s)</h3>
        </div>
        {(http.recent_slow_requests || []).length === 0 ? (
          <div className="px-4 py-10 text-center text-xs text-zinc-500">Aucune requête lente détectée. 🎉</div>
        ) : (
          <div className="divide-y divide-zinc-800/60 text-xs">
            {http.recent_slow_requests.map((r, i) => (
              <div key={i} className="px-4 py-2 flex items-center justify-between hover:bg-white/[0.02]">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400">{r.method}</span>
                  <span className="font-mono text-zinc-200 truncate">{r.path}</span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-zinc-500">{r.status}</span>
                  <span className="text-amber-400 font-bold tabular-nums">{r.duration_ms}ms</span>
                  <span className="text-zinc-600 tabular-nums">{formatTime(r.ts)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
